/* eslint-disable no-unused-expressions */
import { API } from '../../api';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  notification,
} from 'antd';
import { DimaContext } from '../../DimaContext';
import { Link, useRouteMatch } from 'react-router-dom';
import { PlusCircleOutlined } from '@ant-design/icons';
import { USER_ROLE, statusToColorMap } from '../../consts';
import { checkPermission, isAdmin } from '../../utils/util';
import JobBuildInputs from './jobBuildInputs';
import React, { useContext, useEffect, useState } from 'react';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * Lists all jobs for a client and enables users to create unscheduled jobs
 */
const JobTab = props => {
  const { selectedClient } = props;
  const dimaContext = useContext(DimaContext);
  // TODO: create a more specific permission
  const canEdit = process.env.REACT_APP_FEATURE_PERMISSIONS_V2
    ? checkPermission(dimaContext.userData.permissions, 'editClient')
    : isAdmin(USER_ROLE);
  const [form] = Form.useForm();
  const [jobList, setJobList] = useState([]);
  const [allJobsList, setAllJobsList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [inputFields, setInputFields] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [createJobModal, setCreateJobModal] = useState(false);
  const [hasRouteOn, setHasRouteOn] = useState(false);
  const { url } = useRouteMatch();
  const [filtersForm] = Form.useForm();
  const [showFilters, setShowFilters] = useState(false);
  const [jobTypes, setJobTypes] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filters, setFilters] = useState({});

  const validateRouting = routing => {
    try {
      JSON.parse(routing);
      return true;
    } catch (e) {
      notification.error({
        message: 'Error',
        description: 'Routing field must be passed a valid JSON Array',
        duration: 3,
        placement: 'topLeft',
      });
      return false;
    }
  };

  const handleClearForm = () => {
    setInputFields({});
    setHasRouteOn(false);
    // clear form values
    form.setFieldsValue({
      job_type: null,
      status: null,
      routing: null,
    });
  };

  const toggleCreateJobModal = () => {
    handleClearForm();
    setCreateJobModal(!createJobModal);
  };

  const getJobTypes = jobs => {
    const uniqueJobTypes = new Set();
    const jobTypeOptions = [];

    for (const job of jobs) {
      const jt = job.jobType.name;

      if (!uniqueJobTypes.has(jt)) {
        uniqueJobTypes.add(jt);
        jobTypeOptions.push(
          <Option value={jt} key={jt} data-cy={jt}>
            {jt}
          </Option>,
        );
      }
    }

    setJobTypes(jobTypeOptions);
  };

  const getUsers = async () => {
    // get client jobs from DIS API /jobs endpoint
    try {
      const res = await API.get('/user');
      const { users } = res.data;
      setUserList(users);
    } catch (e) {
      notification.error({
        message: 'Error',
        description: 'Unable to get DIMA Users',
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  const getCurrentUser = userId => {
    for (const userObj of userList) {
      if (userObj.id === userId) {
        return userObj.email;
      }
    }

    return null;
  };

  const getJobs = async () => {
    // get client jobs from DIS API /jobs endpoint
    try {
      // TODO: Improve tenant query param for GET /jobs. DIS requires a tenantId, not name, which is not available to
      // dima-backend without an extra query, so we will include that data here for now.
      await getUsers();
      const endpoint = selectedClient.use_multitenant_config
        ? `/dis/jobs?clientId=${selectedClient.id}&tenantId=${selectedClient.tenant.id}`
        : `/dis/jobs?clientId=${selectedClient.id}`;
      const res = await API.get(endpoint);
      const jobs = res.data.results;
      setJobList(jobs);
      setAllJobsList(jobs);
      getJobTypes(jobs);
    } catch (e) {
      notification.error({
        message: 'Error',
        description: `Unable to get Jobs for client: ${selectedClient.name}`,
        duration: 3,
        placement: 'topLeft',
      });
    }
  };

  const getPayload = values => {
    const payload = {};

    for (const field of Object.keys(inputFields)) {
      payload[field] = values[field];
    }

    return payload;
  };

  const onClearFilters = () => {
    filtersForm.resetFields();
    setFilters({});
    setJobList(allJobsList);
    setFiltersApplied(false);
    setShowFilters(false);
  };

  /**
   * Applies filters to clients jobs,
   * returns jobs matching filters,
   * if no matches show all jobs and notify user
   */
  const onSetFilters = () => {
    if (Object.keys(filters).length === 0) {
      setFiltersApplied(false);
    } else {
      const filteredJobList = [];

      for (const job of allJobsList) {
        let matchesFilters = true;

        for (const [key, value] of Object.entries(filters)) {
          // Handles routedWith
          if (key === 'routedWith') {
            let hasRoute = false;

            if (job[key]) {
              const routeString = JSON.stringify(job[key]);

              if (routeString.includes(value)) {
                hasRoute = true;
              }
            }

            if (!hasRoute) {
              matchesFilters = false;
            }
          }

          // Handles datetime calculations
          if (key === 'created_at' || key === 'updated_at') {
            // Add UTC time to date
            const start = new Date(`${value[0]}T00:00:00.000Z`);
            const end = new Date(`${value[1]}T23:59:59.999Z`);
            const jobDate = new Date(job[key]);

            if (jobDate < start || jobDate > end) {
              matchesFilters = false;
            }
          }

          // Handles jobType filters
          if (key === 'jobType') {
            if (job[key].name !== value) {
              matchesFilters = false;
            }
          }

          // Handles status filters
          if (key === 'status') {
            if (job[key] !== value) {
              matchesFilters = false;
            }
          }
        }

        if (matchesFilters) {
          filteredJobList.push(job);
        }
      }

      if (filteredJobList.length > 0) {
        setJobList(filteredJobList);
        setFiltersApplied(true);
      } else {
        notification.error({
          message: 'Error',
          description: 'There were no jobs matching your selected filters, listing all jobs',
          duration: 3,
          placement: 'topLeft',
        });
        onClearFilters();
      }
    }

    setShowFilters(false);
  };

  const onFinishFailed = errorInfo => {
    /* eslint-disable-next-line no-console */
    console.log('Failed: ', errorInfo);
  };

  const onCreateJob = async values => {
    let newJob = {
      jobType: values.job_type,
      payload: getPayload(values),
      userId: dimaContext.userData.userId,
    };

    if (selectedClient.use_multitenant_config) {
      newJob.tenant = selectedClient.tenant.id;
    }

    // This is a breaking change until DIS is deployed with userId added to the jobs entity
    // The feature flag is temporary and is intended to support dev work until DIS is
    // deployed centrally with a version that supports jobs userId
    if (process.env.REACT_APP_INCLUDE_USERID_DIS_JOB) {
      newJob.userId = dimaContext.userData.userId;
    }

    if (hasRouteOn) {
      if (validateRouting(values.routing)) {
        newJob.routing = JSON.parse(values.routing) ? JSON.parse(values.routing) : [];
      } else {
        newJob = null;
      }
    }

    if (newJob !== null) {
      setLoading(true);

      try {
        await API.post(`/dis/jobs?clientId=${selectedClient.id}`, newJob);
        notification.success({
          message: 'Success!',
          description: 'Job created successfully',
          duration: 3,
          placement: 'topLeft',
        });
        await getJobs();
      } catch (e) {
        const err = e.response.data ? JSON.stringify(e.response.data) : e;
        notification.error({
          message: 'Error',
          description: `There was an error creating the Job: ${err}`,
          duration: 3,
          placement: 'topLeft',
        });
      }

      setLoading(false);
      toggleCreateJobModal();
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      ...inputFields,
    });
  }, [inputFields]);

  const generateColumns = () => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        render: jobId => {
          const linkUrl = `${url}/job/${jobId}`;
          return (
            <Link to={linkUrl} data-cy={`job-link-${jobId}`}>
              {jobId}
            </Link>
          );
        },
      },
      {
        title: 'Job Type',
        dataIndex: 'jobType',
        key: 'jobType',
        render: jobType => {
          const linkUrl = `${url}/jobType/${jobType.name}`;
          return (
            <Link to={linkUrl} data-cy={`jobType-link-${jobType.name}`}>
              {jobType.name}
            </Link>
          );
        },
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        defaultSortOrder: 'descend',
        sorter: (a, b) => {
          if (a.created_at === null || b.created_at === null) return 1;
          return new Date(a.created_at) - new Date(b.created_at);
        },
        render: createdAt => {
          return <span>{new Date(createdAt).toUTCString()}</span>;
        },
      },
      {
        title: 'Updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        sorter: (a, b) => {
          if (a.updated_at === null || b.updated_at === null) return 1;
          return new Date(a.updated_at) - new Date(b.updated_at);
        },
        render: updatedAt => {
          return <span>{new Date(updatedAt).toUTCString()}</span>;
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: status => {
          return (
            <span
              style={{
                border: '1px solid',
                borderColor: statusToColorMap[status].border,
                padding: '5px 10px',
                backgroundColor: statusToColorMap[status].background,
                color: statusToColorMap[status].color,
              }}
            >
              {' '}
              {status}{' '}
            </span>
          );
        },
      },
      {
        title: 'Triggered By',
        dataIndex: 'userId',
        key: 'userId',
        render: (userId, record) => {
          if (userId) return getCurrentUser(userId);

          if (record.schedule) {
            const linkUrl = `${url}/schedule/${record.schedule.id}`;
            return (
              <Link to={linkUrl} data-cy={`schedule-${record.schedule.name.replace(' ', '-')}`}>
                {record.schedule.name}
              </Link>
            );
          }

          if (record.parent) {
            const linkUrl = `${url}/job/${record.parent.id}`;
            return (
              <Link to={linkUrl} data-cy={`parent-${record.parent.id}`}>
                {record.parent.id}
              </Link>
            );
          }

          return null;
        },
      },
      {
        title: 'Routed With',
        dataIndex: 'routedWith',
        key: 'routedWith',
        render: routedWith => {
          if (!routedWith) return null;
          return (
            <div>
              <pre>{JSON.stringify(routedWith, null, 2).replace(/[{}"]/g, '')}</pre>
            </div>
          );
        },
      },
    ];
  };

  const addFilter = (key, value) => {
    const newFilters = filters;
    newFilters[key] = value;
    setFilters(newFilters);
  };

  // on mount, fetch all jobs
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setJobList([]);
      await getJobs();
      setLoading(false);
    };

    if (selectedClient) fetchData();
  }, [selectedClient]);

  return (
    <>
      <Modal
        title="Create Job"
        open={createJobModal}
        onCancel={toggleCreateJobModal}
        footer={null}
        style={{ top: 10 }}
        width="80%"
        height="90%"
      >
        <Form
          form={form}
          name="createJob"
          onFinish={onCreateJob}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          validateTrigger="onSubmit"
        >
          <JobBuildInputs
            selectedClient={selectedClient}
            form={form}
            inputFields={inputFields}
            setInputFields={setInputFields}
            hasRouteOn={hasRouteOn}
            setHasRouteOn={setHasRouteOn}
          />

          <Button
            style={{
              background: '#28B5BE',
              color: 'white',
              marginTop: '25px',
            }}
            size="large"
            htmlType="submit"
            data-cy="submitJob"
          >
            Create
          </Button>

          <Button
            style={{
              alignSelf: 'flex-end',
              position: 'absolute',
              right: 35,
              marginTop: '25px',
            }}
            type="default"
            size="large"
            onClick={toggleCreateJobModal}
            data-cy="close-job-btn"
          >
            Close
          </Button>
        </Form>
      </Modal>

      <Modal
        title="Job Filters"
        open={showFilters}
        onOk={onSetFilters}
        onCancel={e => [e.stopPropagation(), setShowFilters(false)]}
        footer={null}
      >
        <Form
          form={filtersForm}
          name="basic"
          layout="horizontal"
          onFinish={onSetFilters}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item name="jobtype_filter" label="Job Type">
            <Select
              defaultValue=""
              data-cy="select-jobType"
              size={12}
              style={{ float: 'right', width: '375px' }}
              onChange={value => addFilter('jobType', value)}
            >
              {' '}
              {jobTypes}
            </Select>
          </Form.Item>

          <Form.Item name="routed_with_filter" label="Routed With">
            <Space direction="vertical" size={12} style={{ float: 'right' }}>
              <Input
                style={{ width: '375px' }}
                onChange={e => addFilter('routedWith', e.target.value)}
              />
            </Space>
          </Form.Item>
          <Form.Item name="status_filter" label="Status">
            <Select
              defaultValue=""
              size={12}
              style={{ float: 'right', width: '375px' }}
              onChange={value => addFilter('status', value)}
            >
              <Option value="Succeeded" data-cy="select_succeeded">
                Succeeded
              </Option>
              <Option value="Failed">Failed</Option>
              <Option value="Processing">Processing</Option>
              <Option value="Partial">Partial</Option>
            </Select>
          </Form.Item>
          <Form.Item name="created_at_filter" label="Created At">
            <Space direction="vertical" size={12} style={{ float: 'right' }}>
              <RangePicker
                style={{ width: '375px' }}
                onChange={(value, dateString) => [addFilter('created_at', dateString)]}
              />
            </Space>
          </Form.Item>
          <Form.Item name="updated_at_filter" label="Updated At">
            <Space direction="vertical" size={12} style={{ float: 'right' }}>
              <RangePicker
                style={{ width: '375px' }}
                onChange={(value, dateString) => [addFilter('updated_at', dateString)]}
              />
            </Space>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              data-cy="dis-apply-filter-btn"
              loading={false}
              style={{ float: 'right' }}
            >
              Apply Filters
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Button
        type="primary"
        htmlType="button"
        data-cy="dis-filter-search-btn"
        onClick={e => {
          filtersApplied
            ? [e.stopPropagation(), onClearFilters(), setShowFilters(false)]
            : [e.stopPropagation(), setShowFilters(true)];
        }}
      >
        {filtersApplied ? 'Clear Filters' : 'Filters'}
      </Button>
      <Button
        style={{
          marginTop: 'auto',
          marginRight: 10,
          float: 'right',
          background: '#28B5BE',
          color: 'white',
        }}
        disabled={!canEdit}
        onClick={() => toggleCreateJobModal()}
        data-cy="create-job-btn"
        size="large"
      >
        <PlusCircleOutlined /> New Job
      </Button>

      <br />
      <br />
      <Spin spinning={isLoading}>
        <Table
          dataSource={[...jobList]}
          columns={generateColumns()}
          pagination={{ pageSize: 100 }}
          loading={isLoading}
          rowClassName={row => (row.status === false ? 'table-row-dark ' : 'table-row-light')}
          showExpandColumn={false}
        />
      </Spin>
    </>
  );
};

export default JobTab;
